import React, { useMemo, useState, Children, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
type TaxKey = 'dependence' | 'opportunity' | 'leakage' | 'visibility' | 'capability' | 'technology';
export function Home() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const taxPickerOptions = useMemo(() => [{
    key: 'dependence' as const,
    label: 'Dependence Tax',
    hint: 'Work stalls on people'
  }, {
    key: 'opportunity' as const,
    label: 'Opportunity Tax',
    hint: <>
            Leaders have no time
            <br className="hidden sm:inline" data-id="element-419" /> for the future
          </>
  }, {
    key: 'leakage' as const,
    label: 'Leakage Tax',
    hint: 'Margin slips quietly'
  }, {
    key: 'visibility' as const,
    label: 'Visibility Tax',
    hint: <>
            Can't trust the data
            <br className="hidden sm:inline" data-id="element-420" /> for decisions
          </>
  }, {
    key: 'capability' as const,
    label: 'Capability Tax',
    hint: "Managers can't carry the load"
  }, {
    key: 'technology' as const,
    label: 'Technology Tax',
    hint: <>
            Software and AI don't
            <br className="hidden sm:inline" data-id="element-421" /> help like they should
          </>
  }], []);
  const [selectedTax, setSelectedTax] = useState<TaxKey | null>(null);
  const whatsWrongAllLink = '/whats-wrong#six-taxes';
  const scrollToElement = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 96;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <main className="w-full" data-id="element-422">
      {/* 1) Hero — renders instantly for fast LCP */}
      <section className="relative px-6 pt-28 pb-14 bg-navy overflow-hidden" data-id="element-423">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-424">
          <BlueprintGrid data-id="element-425" />
        </div>

        <div className="relative w-full max-w-4xl mx-auto z-10 text-center pt-[15px]" data-id="element-426">
          <p className="text-[10px] sm:text-sm uppercase tracking-[0.2em] text-seaglass/60 mb-8 font-medium pt-[25px]" data-id="element-427">
            <span className="block sm:inline" data-id="element-428">Growth is working.</span>
            <span className="block sm:inline sm:ml-1" data-id="element-429">
              But it's not as clean as it used to be.
            </span>
          </p>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-semibold text-white mb-8 leading-tight" data-id="element-430">
            <span className="text-white/70 font-normal" data-id="element-431">
              Every founder eventually asks:{' '}
            </span>
            Why is growth starting to feel like it's working against us?
          </h1>

          <p className="text-xl md:text-2xl text-white/90 mx-auto mb-6 font-medium" data-id="element-432">
            Decisions keep coming back to you. Work gets stuck. Margin slips.
          </p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-4" data-id="element-433">
            <Link to="/book" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-434">
              <span data-id="element-435">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-436" />
            </Link>
          </motion.div>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-sm text-white/55" data-id="element-437">
            45 minutes. No pitch. Just clarity.
          </motion.p>
        </div>
      </section>

      {/* 2) Tax Picker Band */}
      <section className="relative px-6 sm:pt-16 pb-20 sm:pb-24 bg-navy pt-[32px]" data-id="element-438">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl px-6" data-id="element-439">
          <div className="h-px bg-white/10" data-id="element-440" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={stagger} className="relative w-full max-w-5xl mx-auto z-10" data-id="element-441">
          <div className="mt-10 mb-6" data-id="element-442" />

          <motion.div variants={fadeIn} className="text-center mb-4" data-id="element-443">
            <p className="md:text-[28px] font-serif font-semibold text-white/90 text-[28px]" data-id="element-444">
              If you&apos;re being honest, which of these is creating the most
              drag right now?
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="text-center mb-8" data-id="element-445">
            <p className="text-lg text-white/70 mx-auto max-w-3xl" data-id="element-446">
              We help founder-led companies find and fix the real constraint
              making the business heavier, slower, and too dependent on the
              founder.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-447">
            {taxPickerOptions.map((t, i) => {
            const isSelected = t.key === selectedTax;
            return <Link key={t.key} to={`/whats-wrong#${t.key}`} onClick={() => {
              setSelectedTax(t.key);
              setTimeout(() => scrollToElement(`tax-${t.key}`), 0);
            }} className={['flex flex-col min-h-[160px] px-7 py-6 transition-colors duration-200', isSelected ? 'bg-white/[0.07] border border-seaglass/40' : 'bg-white/[0.04] border border-white/[0.12] hover:border-rust/40'].join(' ')} data-id="element-448">
                  <p className="text-white font-medium text-lg leading-snug" data-id="element-449">
                    {t.hint}
                  </p>
                  <div className="mt-auto pt-4" data-id="element-450">
                    <div className="h-px bg-rust/30 mb-3" data-id="element-451" />
                    <p className="text-sm font-mono uppercase tracking-wider text-seaglass/60" data-id="element-452">
                      {t.label}
                    </p>
                  </div>
                </Link>;
          })}
          </motion.div>

          <motion.div variants={fadeIn} className="mt-8 text-center" data-id="element-453">
            <Link to="/whats-wrong#six-taxes" className="inline-flex items-center space-x-2 text-sm font-medium transition-colors duration-200 hover:text-white" style={{
            color: '#A8C4BB'
          }} data-id="element-454">
              <span data-id="element-455">See all Six Structural Taxes</span>
              <ArrowRight className="w-3.5 h-3.5" data-id="element-456" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* 3) Recognition */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-457">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-458">
          <div className="max-w-3xl" data-id="element-459">
            <motion.div variants={fadeIn} data-id="element-460">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-461">
                If this feels familiar, you are probably not dealing with a
                motivation problem.
              </h2>
              <div className="space-y-3 text-body text-text" data-id="element-462">
                <p data-id="element-463">The business is still working.</p>
                <p data-id="element-464">
                  Revenue may still be growing. Customers may still be buying.
                  The team may still be holding it together.
                </p>
                <p data-id="element-465">But something underneath has changed.</p>
                <p data-id="element-466">
                  What used to work no longer works cleanly. Growth added
                  volume. Growth added complexity. And now too much depends on
                  escalation, heroics, and founder rescue.
                </p>
                <p className="font-medium text-navy mt-2" data-id="element-467">
                  What feels manageable now gets more expensive and more
                  entrenched as you scale.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 4) How the drag shows up */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-468">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-469">
          <div className="max-w-3xl" data-id="element-470">
            <motion.div variants={fadeIn} className="mb-12" data-id="element-471">
              <p className="text-xs font-mono uppercase tracking-wider text-seaglass mb-4" data-id="element-472">
                The Pattern
              </p>
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-473">
                How the drag shows up
              </h2>
            </motion.div>

            <motion.ul variants={fadeIn} className="space-y-3 mb-10" data-id="element-474">
              {['Decisions keep climbing back to you', 'The client experience depends on who is involved', 'Great people are burning time on rework and ambiguity', 'Exceptions keep piling up', 'Managers spend too much time routing work', 'Revenue grows, but margins keep thinning'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-475">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-476" />
                  <span className="text-base text-text font-medium" data-id="element-477">
                    {item}
                  </span>
                </li>)}
            </motion.ul>

            <motion.div variants={fadeIn} className="space-y-4" data-id="element-478">
              <p className="text-body text-text" data-id="element-479">
                This is the point where many companies add people, meetings,
                tools, or layers.
              </p>
              <p className="text-body text-text" data-id="element-480">
                Sometimes that helps for a minute.
              </p>
              <p className="text-body text-navy font-medium" data-id="element-481">
                But when the drag is coming from the way work resolves, adding
                more usually adds more load.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 5) What we do instead */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-482">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-483">
          <div className="max-w-3xl" data-id="element-484">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-485">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-486">
                What we do instead
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-6 text-body text-text" data-id="element-487">
              <p data-id="element-488">
                We find the constraint behind the drag and fix the right thing
                first.
              </p>
              <ul className="space-y-3" data-id="element-489">
                {['Not a giant overhaul', 'Not more process for the sake of process', 'Not another tool rollout'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-490">
                    <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-491" />
                    <span className="text-text" data-id="element-492">{item}</span>
                  </li>)}
              </ul>
              <p className="font-medium text-navy" data-id="element-493">
                The goal is a business where decisions resolve lower, managers
                carry more of the load cleanly, the numbers support decisions
                instead of debate, and growth stops pulling the founder back
                into the middle.
              </p>
              <p className="font-medium text-navy" data-id="element-494">
                Technology follows clarity. We do not automate confusion.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 7) Built from the inside */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-495">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-496">
          <motion.div variants={fadeIn} className="flex items-center gap-5 mb-8" data-id="element-497">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/nmtzzfh8cozmkaychcuzkb/a0ec6fac-7fa4-4bbc-a56f-9030015af40e/Westley_Family_25_stevecowellphoto-178.webp" alt="John Westley" className="w-20 h-20 rounded-full object-cover object-top grayscale flex-shrink-0" data-id="element-498" />
            <div data-id="element-499">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-navy leading-tight" data-id="element-500">
                Built from the inside
              </h2>
              <Link to="/about-john" className="inline-flex items-center space-x-1 mt-1 text-sm text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-501">
                <span data-id="element-502">About John</span>
                <ArrowRight className="w-3.5 h-3.5" data-id="element-503" />
              </Link>
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-4 text-body text-text" data-id="element-504">
            <p data-id="element-505">This perspective was built inside real growth.</p>
            <p data-id="element-506">
              For 15+ years, this work was shaped across growth, revenue,
              operations, systems, events, and executive leadership as
              businesses got bigger, faster, and more complex.
            </p>
            <p data-id="element-507">
              That includes working across the business as Tom Ferry scaled from
              roughly $6M to $84M, then serving as President of Jon Gordon
              Companies translating founder vision into structure, workflows,
              and clearer execution.
            </p>
            <p className="font-semibold text-navy" data-id="element-508">
              That is where this offer comes from.
            </p>
            <p data-id="element-509">
              When growth gets messy, the bottleneck is rarely sitting neatly
              inside one department. It usually lives in the seams. That is why
              this work is built to find what is actually creating the drag
              before you add more load.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* 8) How we find the problem */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-510">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-511">
          <div className="max-w-3xl" data-id="element-512">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-513">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-514">
                We start wide, then go deep.
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-8" data-id="element-515">
              <div className="space-y-4 text-body text-text" data-id="element-516">
                <p data-id="element-517">
                  We take a fast pass across the few flows carrying the business
                  end to end, then zoom in where the drag is actually being
                  created.
                </p>
                <p data-id="element-518">
                  That keeps us from treating the loudest symptom like the
                  source.
                </p>
              </div>

              <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-519">
                <p className="text-body text-text font-medium" data-id="element-520">
                  We are not looking for everything that could be improved.
                </p>
                <p className="text-body text-text mt-2" data-id="element-521">
                  We are looking for the next domino.
                </p>
              </div>

              <p className="text-body text-navy font-medium" data-id="element-522">
                If the founder is still the only one who can decide, something
                is missing.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 9) Start here */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-523">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto text-center md:text-left" data-id="element-524">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-525">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-526">
              Start here
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="bg-white border border-seaglass/15 p-8 md:p-12" data-id="element-527">
            <h3 className="text-2xl font-serif font-semibold text-navy mb-4" data-id="element-528">
              Honest Conversation
            </h3>
            <p className="text-body text-text mb-8" data-id="element-529">
              A 45-minute working conversation to figure out whether this is a
              real business problem or just normal growth friction.
            </p>

            <div className="mb-10 text-left" data-id="element-530">
              <p className="text-sm font-semibold text-navy mb-4" data-id="element-531">
                You leave with:
              </p>
              <ul className="space-y-3" data-id="element-532">
                {['a clearer read on what is really going on', 'whether the drag looks structural or situational', 'the next right step, including "keep pushing" if that is the truth'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-533">
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-534" />
                    <span className="text-base text-text" data-id="element-535">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="text-left" data-id="element-536">
              <Link to="/book" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-537">
                <span data-id="element-538">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-539" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* 10) How we work */}
      <section className="py-24 px-6 grain bg-paper-dark" data-id="element-540">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-541">
          <div className="max-w-4xl" data-id="element-542">
            <motion.div variants={fadeIn} className="mb-16" data-id="element-543">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-544">
                How we work
              </h2>
              <p className="text-lg text-text" data-id="element-545">
                This is a sequence, not a menu.
              </p>
            </motion.div>

            <div className="relative" data-id="element-546">
              <div className="absolute left-8 top-8 bottom-8 w-px bg-seaglass/20 hidden md:block" data-id="element-547" />

              <div className="space-y-12" data-id="element-548">
                {[{
                num: '1',
                title: 'Honest Conversation',
                desc: 'We sort signal from noise and decide whether anything is worth diagnosing.',
                outcome: 'Clarity and a real next-step decision.'
              }, {
                num: '2',
                title: 'Constraint Diagnostic',
                desc: 'If the issue is deeper than normal friction, we run a focused diagnostic to identify the primary constraint with evidence.',
                outcome: 'Constraint, evidence, options, and a clear recommendation.'
              }, {
                num: '3',
                title: 'Focused Redesign',
                desc: 'If fixing it together makes sense, we redesign one important part of the business so it runs with less drag, less founder pull, and clearer ownership.',
                outcome: 'A scoped fix the team can actually run without you back in the middle.'
              }].map((phase, i) => <motion.div key={i} variants={fadeIn} className="relative flex items-start space-x-6" data-id="element-549">
                    <div className="flex-shrink-0 w-16 h-16 bg-navy flex items-center justify-center text-white font-semibold text-xl relative z-10" data-id="element-550">
                      {phase.num}
                    </div>
                    <div className="flex-1 pt-2" data-id="element-551">
                      <h3 className="text-xl font-semibold text-navy mb-2" data-id="element-552">
                        {phase.title}
                      </h3>
                      <p className="text-body text-text mb-2" data-id="element-553">{phase.desc}</p>
                      <p className="text-sm text-text-muted" data-id="element-554">
                        <strong className="font-semibold text-navy" data-id="element-555">
                          Outcome:
                        </strong>{' '}
                        {phase.outcome}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            <motion.div variants={fadeIn} className="mt-12 text-left md:ml-22" data-id="element-556">
              <Link to="/how-we-fix-it" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-557">
                <span data-id="element-558">See How We Fix It</span>
                <ArrowRight className="w-4 h-4" data-id="element-559" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 11) Six Taxes - teaser grid */}
      <section id="six-taxes" className="relative py-24 px-6 bg-[#A8C4BB26] overflow-hidden" data-id="element-560">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-561" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto relative z-10" data-id="element-562">
          <motion.div variants={fadeIn} className="text-center mb-16" data-id="element-563">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-564">
              Where is the drag showing up most?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-565">
            {[{
            key: 'dependence' as const,
            name: 'Dependence Tax',
            desc: 'Too much still depends on the founder or a few key people',
            feels: "You're one absence away from stuck.",
            commonMisread: "You think it's a people problem. It's usually trapped logic.",
            validate: 'where decisions rely on memory instead of shared rules.'
          }, {
            key: 'opportunity' as const,
            name: 'Opportunity Tax',
            desc: 'Leadership has no room for the future',
            feels: "You can't protect time to build what's next.",
            commonMisread: "You think it's a planning problem. It's usually a load path problem.",
            validate: 'what keeps routing strategic work back into today.'
          }, {
            key: 'leakage' as const,
            name: 'Leakage Tax',
            desc: 'Margin is leaking through exceptions and rework',
            feels: "You're busy… but profitability doesn't follow.",
            commonMisread: "You think it's pricing. It's often unmanaged variance.",
            validate: 'where exceptions enter and become precedent.'
          }, {
            key: 'visibility' as const,
            name: 'Visibility Tax',
            desc: 'The numbers create debate instead of decisions',
            feels: "You can't tell what's true fast enough to move.",
            commonMisread: 'You think you need better dashboards. You usually need better definitions.',
            validate: "which metrics can't be trusted - and why."
          }, {
            key: 'capability' as const,
            name: 'Capability Tax',
            desc: 'Managers cannot carry the load cleanly',
            feels: 'Leaders spend all day routing work instead of leading.',
            commonMisread: 'You think you need stronger managers. You may need clearer ownership.',
            validate: 'where accountability breaks under real complexity.'
          }, {
            key: 'technology' as const,
            name: 'Technology Tax',
            desc: 'Tools and AI are not creating real leverage',
            feels: "You're paying for tools and still doing it the hard way.",
            commonMisread: 'You think you need different tools. You usually need clearer rules.',
            validate: 'where workflows and decision logic are too unclear to automate.'
          }].map((tax, i) => {
            const isSelected = selectedTax === tax.key;
            const hasSelection = selectedTax !== null;
            const isDimmed = hasSelection && !isSelected;
            return <motion.div key={tax.key} variants={fadeIn} animate={{
              opacity: isDimmed ? 0.45 : 1,
              scale: isSelected ? 1.02 : isDimmed ? 0.98 : 1
            }} transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1]
            }} data-id="element-566">
                  <Link id={`tax-${tax.key}`} to={`/whats-wrong#${tax.key}`} className={['group block p-8 bg-white border min-h-[200px] flex flex-col h-full transition-shadow duration-300', isSelected ? 'border-seaglass/50 ring-2 ring-seaglass/35 shadow-lg shadow-seaglass/10' : hasSelection ? 'border-seaglass/10' : 'border-seaglass/10 hover:border-seaglass/30 hover:-translate-y-1 transition-all duration-300'].join(' ')} data-id="element-567">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-568">
                      <span className="text-xs font-mono font-semibold text-seaglass uppercase tracking-wider" data-id="element-569">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-570">
                      {tax.name}
                    </h3>
                    <p className="text-sm text-text leading-relaxed mb-3" data-id="element-571">
                      {tax.desc}
                    </p>
                    <div className="mt-auto pt-4 space-y-4" data-id="element-572">
                      <div className="pt-1" data-id="element-573">
                        <span className="text-sm font-medium text-seaglass group-hover:text-seaglass-light transition-colors duration-300" data-id="element-574">
                          Read the full signals →
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>;
          })}
          </div>

          <motion.div variants={fadeIn} className="text-center" data-id="element-575">
            <Link to={whatsWrongAllLink} className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-576">
              <span data-id="element-577">See all Six Structural Taxes</span>
              <ArrowRight className="w-4 h-4" data-id="element-578" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* 12) Quick fit check */}
      <section className="py-24 px-6 bg-paper" data-id="element-579">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-580">
          <motion.div variants={fadeIn} className="mb-12 text-center md:text-left" data-id="element-581">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-582">
              Quick fit check
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16" data-id="element-583">
            <motion.div variants={fadeIn} data-id="element-584">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-585">
                This is for you if…
              </h3>
              <ul className="space-y-3" data-id="element-586">
                {['You are still growing, but the business feels heavier than it should', 'Decisions keep climbing back to the founder', 'The same issues keep showing up in different forms', 'Great people are carrying too much ambiguity', 'Margin is thinning even while revenue grows'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-587">
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-588" />
                    <span className="text-base text-text" data-id="element-589">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeIn} className="md:pl-12 md:border-l md:border-seaglass/15" data-id="element-590">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-591">
                This is not for you if…
              </h3>
              <ul className="space-y-3" data-id="element-592">
                {['You want someone to run operations for you long-term', 'You mainly need more capacity, not less drag', 'You want a tool rollout, framework rollout, or quick hack as the fix', 'You want an ongoing dependency, not a clean solution'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-593">
                    <X className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-594" />
                    <span className="text-base text-text" data-id="element-595">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.p variants={fadeIn} className="text-sm text-text-muted text-center mt-12" data-id="element-596">
            If you are not sure which camp you are in, that is exactly what the
            conversation is for.
          </motion.p>
        </motion.div>
      </section>

      {/* 13) Mini FAQ */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-597">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-598">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-599">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-600">
              A few things worth knowing
            </h2>
          </motion.div>

          <div className="space-y-0" data-id="element-601">
            {/* Q1 */}
            <motion.div variants={fadeIn} className="py-8 border-b border-seaglass/10" data-id="element-602">
              <h3 className="text-xl font-semibold text-navy mb-3" data-id="element-603">
                Is this like EOS, Scaling Up, or another business framework?
              </h3>
              <p className="text-body text-text mb-4" data-id="element-604">
                No. Frameworks can be useful, but this work starts earlier. It
                helps determine what is actually broken and what needs to change
                before any framework, operator, or tool is likely to help. If
                the company already knows the problem, a framework may be the
                right move. If nobody can cleanly explain why growth feels
                heavier, that is where this work fits.
              </p>
              <Link to="/how-we-fix-it" className="inline-flex items-center space-x-1 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-605">
                <span data-id="element-606">See how the Constraint Diagnostic works</span>
                <ArrowRight className="w-3.5 h-3.5" data-id="element-607" />
              </Link>
            </motion.div>

            {/* Q2 */}
            <motion.div variants={fadeIn} className="py-8 border-b border-seaglass/10" data-id="element-608">
              <h3 className="text-xl font-semibold text-navy mb-3" data-id="element-609">
                Is this fractional COO work?
              </h3>
              <p className="text-body text-text mb-4" data-id="element-610">
                No. A fractional COO usually helps run the business over time.
                This work is narrower and more targeted — identify the
                structural issue behind the recurring friction, redesign it, and
                leave behind something the business can run without ongoing
                dependence.
              </p>
              <Link to="/about-john" className="inline-flex items-center space-x-1 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-611">
                <span data-id="element-612">About John</span>
                <ArrowRight className="w-3.5 h-3.5" data-id="element-613" />
              </Link>
            </motion.div>

            {/* Q3 */}
            <motion.div variants={fadeIn} className="py-8" data-id="element-614">
              <h3 className="text-xl font-semibold text-navy mb-3" data-id="element-615">
                Is this an operating system for my business?
              </h3>
              <p className="text-body text-text mb-4" data-id="element-616">
                Not exactly. Sometimes the work leads to clearer decision rules,
                better handoffs, or tighter operating rhythm. But the job is not
                to install a proprietary system. It is to find the structural
                issue creating drag and fix the part that makes everything else
                harder.
              </p>
              <Link to="/whats-wrong" className="inline-flex items-center space-x-1 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-617">
                <span data-id="element-618">See the Six Structural Taxes</span>
                <ArrowRight className="w-3.5 h-3.5" data-id="element-619" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 15) Final CTA */}
      <section className="relative py-24 px-6 bg-navy overflow-hidden" data-id="element-620">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-621">
          <BlueprintGrid data-id="element-622" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="relative max-w-4xl mx-auto text-center z-10" data-id="element-623">
          <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-serif font-semibold text-white mb-6 leading-tight" data-id="element-624">
            Growth is still working. It should feel clearer, lighter, and less
            dependent on you than this.
          </motion.h2>

          <motion.p variants={fadeIn} className="text-lg text-white/80 mb-10" data-id="element-625">
            Start with a conversation.
          </motion.p>

          <motion.div variants={fadeIn} data-id="element-626">
            <Link to="/book" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-627">
              <span data-id="element-628">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-629" />
            </Link>
            <p className="text-sm text-white/55 mt-4" data-id="element-630">
              45 minutes. No pitch. Just clarity.
            </p>
          </motion.div>
        </motion.div>
      </section>
    </main>;
}